/* eslint-disable */
import axios from 'axios';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { t } from '@web-solutions/module-localization';
import Analytics from '@web-solutions/module-analytics';
import { PaymentProcessor, PaymentProcessorRef } from 'core/payment';
import { init as initBilling, initSpecialOfferEndDate } from 'core/store/billing/actions';
import { selectProducts, selectSpecialOfferEndDate } from 'core/store/billing/selectors';
import { getPaymentErrorAnalyticsParams } from 'core/payment/utils/payment-error-state';
import { getLocalizedText } from 'core/utils/localized';
import getLocale from 'src/localization/get-locale';
import { useFixedButton } from 'core/hooks/use-fixed-button';
import { useRemoteConfig } from 'core/hooks/use-remote-config';
import { useAppSelector } from '@web-solutions/store/types';
import { useNav } from 'src/onboarding/hooks/nav';
import { useHairstyleWomenCheckout } from 'src/onboarding/hooks/useHairstyleWomenCheckout';
import { useCompleteRegistrationEvent } from 'src/onboarding/hooks/useCompleteRegistrationEvent';
import { EmailPopup } from 'src/onboarding/components/ui/email-popup/EmailPopup';
import { useStore } from 'src/onboarding/store/store';
import { useFrontPhoto } from '../_shared/useFrontPhoto';
import { getEnvUrl } from 'src/onboarding/helpers/getEnvUrl';
import { buildPurchaseUserData } from 'src/onboarding/helpers/buildPurchaseUserData';
import { trackGoogleAdsTrialConversion } from 'src/onboarding/helpers/googleAdsConversion';
import rev1 from 'src/onboarding/assets/images/avatar1.webp';
import rev2 from 'src/onboarding/assets/images/avatar2.webp';
import rev3 from 'src/onboarding/assets/images/avatar3.webp';
import { ReactComponent as VisaIcon } from 'core/payment/base/body/icons/cards/visa.svg';
import { ReactComponent as MastercardIcon } from 'core/payment/base/body/icons/cards/mastercard.svg';
import { ReactComponent as AmexIcon } from 'core/payment/base/body/icons/cards/americanexpress.svg';
import { ReactComponent as DiscoverIcon } from 'core/payment/base/body/icons/cards/discover.svg';
import { ReactComponent as JcbIcon } from 'core/payment/base/body/icons/cards/jcb.svg';
import { PrimaryButton } from '../_shared/PrimaryButton';
import { SpecialOfferPopup } from '../_shared/SpecialOfferPopup';
import { reviewImg } from '../_shared/hairstyleWomen';
import type { HairstyleWomenResult } from '../_shared/hairstyleWomenAnalysis';
import styles from './style.module.scss';

const r1 = (v: number) => Math.round(v * 10) / 10;
const level = (v: number) => (v >= 7.5 ? 'high' : v >= 5.5 ? 'mid' : 'low');
const levelColor = (l: string) => (l === 'high' ? 'var(--hsw-good)' : l === 'low' ? 'var(--hsw-bad)' : 'var(--hsw-pink-2)');

// "$0.99" / 0.99 / "" -> 0.99 / 0. Used to tell a real price from a zero the
// billing mapper produced for a trial that has no separate intro price.
const amountOf = (value: any): number => {
  const parsed = Number(String(value ?? '').replace(/[^\d.]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

const SUPPORT_EMAIL = 'support@hairio.app';
// Seller of record. Must stay word for word what the Privacy Policy and the
// Terms of Use name as the company and its registered address — those two pages
// are the source of truth (see pages/privacy-policy, pages/terms-of-use).
// Same values as the men's paywall (hairstyle-paywall).
const COMPANY = 'POFKARIO HOLDINGS LIMITED';
const COMPANY_ADDRESS = 'Thessalonikis 43, office/flat 301 Thessaloniki Court, 3025 Limassol, Cyprus';

const VALUE_KEYS = ['overall', 'breakdown', 'plan', 'tracking'];
const FEATURES = [
  { key: 'plan', emoji: '✨' },
  { key: 'routine', emoji: '🧴' },
  { key: 'progress', emoji: '📈' },
  { key: 'coach', emoji: '🤖' },
];
// n → the custom photo (gender-specific) with a fallback to the stock avatar
const FALLBACK = [rev2, rev3, rev1];
const reviewPhoto = (n: number, g: string) =>
  reviewImg(`review-${n}-${g}`) || reviewImg(`review-${n}`) || FALLBACK[n - 1];
const REVIEWS = [
  { n: 1, nameKey: 'r1Name', textKey: 'r1Text' },
  { n: 2, nameKey: 'r2Name', textKey: 'r2Text' },
  { n: 3, nameKey: 'r3Name', textKey: 'r3Text' },
];

const EMAIL_POPUP_KEY = 'hairstyle_women_email_popup_shown';

function Page() {
  useHairstyleWomenCheckout();
  // Deferred-email A/B (collectEmailAfterPayment): user pays anonymously and submits
  // email only after payment, so fire the FB `CompleteRegistration` pixel on paywall
  // view instead of on email submit (no-op when the flag is off).
  useCompleteRegistrationEvent();
  const { showEmailPopup, paymentSystem, collectEmailAfterPayment, routeGroups, autoRefundAfterPurchase, specialOffer, showSpecialOffer } = useRemoteConfig() as any;
  const { currentIndex, onNext } = useNav();
  const locale = getLocale();
  const dispatch = useDispatch();
  const setPage = useStore((s: any) => s.setPage);
  const hairstyleWomenPlan = useStore((s: any) => s.hairstyleWomenPlan);
  const setHairstyleWomenPlan = useStore((s: any) => s.setHairstyleWomenPlan);
  const front = useFrontPhoto();
  const gender = useStore((s: any) => s.hairstyleWomenGender);
  const g = gender === 'female' ? 'woman' : 'man';
  const res = useStore((s: any) => s.hairstyleWomenResult) as HairstyleWomenResult | null;
  const symmetry = res?.symmetry ?? 8;
  const skin = res?.skin ?? 5;
  const overall = res?.overall ?? 7;
  const { apiBaseUrl } = getEnvUrl();

  const ppRef = useRef<PaymentProcessorRef>(null);
  // selectProducts switches to specialOffer.products once the offer timer is
  // started (specialOfferEndDate). Same rule as the summary pages: the first
  // window shows the regular product, and after the user has closed it
  // `showAfterCloseAmount` times the next open shows the special offer.
  const products = useSelector(selectProducts);
  const specialOfferEndDate = useSelector(selectSpecialOfferEndDate);
  const [paymentClosedCount, setPaymentClosedCount] = useState(0);
  // `showSpecialOffer`: on top of the silent price switch, tell the user about
  // the discount in a bottom sheet once the payment window has been closed.
  const [isSpecialOfferOpen, setIsSpecialOfferOpen] = useState(false);
  // CTA tapped before billing finished loading: spinner in the button and
  // the window opens by itself as soon as the real product (and with it the
  // PaymentProcessor) exist. The dev-preview stand-in doesn't count.
  const [pendingOpen, setPendingOpen] = useState(false);
  const orderDetails = useAppSelector((state: any) => state.billing.orderDetails);
  const isApiReady = useAppSelector((state: any) => state.profile.authorized);
  const profileEmail = useSelector((state: any) => state.profile?.email);

  // sticky CTA appears once the inline (report-card) CTA scrolls out of view
  const { fixed: showSticky } = useFixedButton({ buttonsSelector: '.sub-static-btn' });

  // Email popup — shown once per session when the remote-config flag is on.
  const [isEmailPopupOpen, setIsEmailPopupOpen] = useState(false);

  useEffect(() => {
    Analytics.trackEvent(`step${currentIndex + 1}`, 'view', { page: 'hairstyle-women-paywall' });
  }, []);

  useEffect(() => {
    if (showEmailPopup && !sessionStorage.getItem(EMAIL_POPUP_KEY)) {
      setIsEmailPopupOpen(true);
    }
  }, [showEmailPopup]);

  const handleEmailPopupClose = () => {
    setIsEmailPopupOpen(false);
    sessionStorage.setItem(EMAIL_POPUP_KEY, 'true');
  };

  useEffect(() => {
    if (isApiReady) dispatch(initBilling());
  }, [dispatch, isApiReady]);

  const activeProduct = useMemo(() => {
    const real =
      products.find((p: any) => p.id === hairstyleWomenPlan) ||
      products.find((p: any) => p.default) ||
      products[0];
    if (!real && process.env.NODE_ENV === 'development') {
      const title = '3-Day Trial';
      return {
        id: 'dev-preview', default: true, title: { en: title },
        productTitle: { en: title, de: title, es: title, fr: title, it: title, pt: title, cs: title, id: title, tr: title },
        trialPrice: '$0.00', trialDays: 3, priceText: '$9.99', price: '$9.99', period: 'P1W', currency: 'USD', isTrial: true,
        // the payment modal reads these per-locale on a trial product
        trialText: { en: '95% discount applied' }, aboutTrialText: { en: '3-day trial' },
      } as any;
    }
    return real;
  }, [products, hairstyleWomenPlan]);
  const isPaymentReady = !!activeProduct && activeProduct.id !== 'dev-preview';

  useEffect(() => {
    if (products.length > 0 && !products.find((p: any) => p.id === hairstyleWomenPlan) && activeProduct?.id) {
      setHairstyleWomenPlan(activeProduct.id);
    }
  }, [products, activeProduct, hairstyleWomenPlan, setHairstyleWomenPlan]);

  // Offer-expiry date in the legal block under the CTA: today + the trial length.
  const planExpiresDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + (Number(activeProduct?.trialDays) || 3));
    // Formatted in the funnel's language ("24. September 2026" in de, "24 septembre 2026" in fr).
    return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(d);
  }, [activeProduct, locale]);

  // Billing sentence, same contract as the men's paywall: localized copy with
  // placeholders, every figure taken from the product in remote config.
  //
  // What is charged today needs care — a Stripe subscription carries its trial
  // as trial_period_days with trial_price_amount = 0 (the intro amount is a
  // separate charge), and react-billing turns that zero into "$0" (see
  // packages/react-billing/utils/prices.ts). So the config's own
  // priceStartTrial.billedAmount wins, and the mapped value is used only when
  // it is a real, non-zero amount.
  const priceTerms = useMemo(() => {
    const p: any = activeProduct || {};
    const trialPrice =
      p.priceStartTrial?.billedAmount ||
      (amountOf(p.trialPrice) > 0 ? p.trialPrice : '') ||
      (amountOf(p.trialPriceAmount) > 0 ? `$${amountOf(p.trialPriceAmount).toFixed(2)}` : '') ||
      '';
    const planTitle =
      getLocalizedText(p.title, locale) || getLocalizedText(p.productTitle, locale);
    const fullPrice =
      getLocalizedText(p.priceStartTrial?.price, locale) || p.priceText || p.price || '';

    // Until the products have loaded the sentence would read "charged  for , then ."
    // — render nothing instead of a sentence with empty figures.
    if (!trialPrice || !fullPrice) return '';

    // Function replacers: a price like "$19.99" in a replacement string would
    // be read as a capture-group reference.
    return t('hairstyleWomenPaywall.priceTerms')
      .replace('{date}', () => planExpiresDate)
      .replace('{trialPrice}', () => trialPrice)
      .replace('{plan}', () => planTitle)
      .replace('{price}', () => fullPrice)
      .replace('{email}', () => SUPPORT_EMAIL);
  }, [activeProduct, planExpiresDate, locale]);

  const onPaymentSuccess = async (_purchase?: any, paymentContext?: { email?: string }) => {
    // autoRefundAfterPurchase (remote kill-switch): service unavailable, payment
    // auto-refunded — nothing to collect or upsell. Short-circuit straight to
    // onNext() so nav.ts opens the refund modal and sends the single needRefund
    // update-by-token; skipping this handler's own update avoids a duplicate,
    // non-needRefund request racing it.
    if (autoRefundAfterPurchase) {
      Analytics.trackEvent('hairstyleWomenPayment', 'success', { note: 'auto-refund' });
      onNext();
      return;
    }

    // Google Ads trial conversion (DEV-1138). Fires only for real purchases;
    // auto-refunded ones above are not sales.
    trackGoogleAdsTrialConversion(_purchase, activeProduct);

    const { userId: currentUserId, email: editedEmail, setEmail } = useStore.getState();

    // Stripe deferred-email + Apple Pay: the Apple Pay sheet may return an email
    // (stashed on window by ApplePayButton). Use it and skip email-after-payment.
    const applePayEmail =
      collectEmailAfterPayment && paymentSystem === 'stripe'
        ? (window as any).__apple_pay_payer_email__
        : undefined;
    const isValidApplePayEmail =
      typeof applePayEmail === 'string' && /\S+@\S+\.\S+/.test(applePayEmail);
    (window as any).__apple_pay_payer_email__ = undefined;

    const paymentEmail =
      (isValidApplePayEmail ? applePayEmail : undefined) ||
      paymentContext?.email ||
      _purchase?.email ||
      profileEmail ||
      editedEmail;

    // Keep the shared state.email in sync so the downstream upsale-dynamic page
    // (which reads state.email) has an email to send.
    if (paymentEmail && paymentEmail !== editedEmail) setEmail(paymentEmail);

    // Deferred-email flow (Variant B, "Перенос сбора email на пост-покупку"): no
    // email captured yet → collect it on the shared email-after-payment page, which
    // sends the stashed minimal payload (+ the entered email) and routes on to
    // hairstyle-women-success. Apple Pay that already returned an email skips the page.
    if (collectEmailAfterPayment && paymentSystem === 'stripe' && !paymentEmail) {
      useStore.getState().setPendingPurchasePayload({
        subscription: { isActive: true },
        responseWithAuthTokens: true,
      });
      useStore.getState().setPendingPostPaymentPage('hairstyle-women-success');
      setPage('email-after-payment');
      return;
    }

    if (isValidApplePayEmail) {
      useStore.getState().addSkippedPage('email-after-payment');
    }

    if (!currentUserId) {
      Analytics.trackEvent('hairstyleWomenPayment', 'success', { plan: hairstyleWomenPlan, note: 'no-uid' });
      onNext();
      return;
    }

    try {
      const userData = buildPurchaseUserData({
        email: paymentEmail,
        store: useStore.getState(),
        flow: routeGroups,
      });
      const response = await axios.put(`${apiBaseUrl}/user/update-by-token/${currentUserId}`, userData);
      // Save auth tokens so the one-click upsale (upsale-dynamic) can charge.
      const { refreshToken, accessToken } = response.data || {};
      if (accessToken && refreshToken) {
        useStore.getState().setAuthTokens({ refreshToken, accessToken, userId: currentUserId });
      }
      Analytics.trackEvent('hairstyleWomenPayment', 'success', { plan: hairstyleWomenPlan });
    } catch (error) {
      const e = error as any;
      Analytics.trackEvent('hairstyleWomenPayment', 'updateError', {
        message: e?.response?.data?.message || e?.message, status: e?.response?.status,
      });
    }
    onNext();
  };

  const onPaymentError = (error: any) => {
    // Clear any stashed Apple Pay email on error so a following card payment
    // doesn't skip email-after-payment with a stale value.
    (window as any).__apple_pay_payer_email__ = undefined;
    Analytics.trackEvent('hairstyleWomenPayment', 'error', getPaymentErrorAnalyticsParams(error));
  };

  const onPaymentClose = () => {
    const closed = paymentClosedCount + 1;
    setPaymentClosedCount(closed);
    const showAfterClose = Number(specialOffer?.showAfterCloseAmount) || 1;
    // The end date is persisted in the browser and never cleared, so an offer
    // started in an earlier session (or before this popup existed) would block
    // the popup forever. Only a timer that is still running counts as started.
    const offerRunning =
      !!specialOfferEndDate && new Date(specialOfferEndDate as any).getTime() > Date.now();
    if (specialOffer?.enabled && !offerRunning && closed >= showAfterClose) {
      Analytics.trackEvent('hairstylePaywall', 'special_offer', { closed });
      dispatch(initSpecialOfferEndDate());
      if (showSpecialOffer) setIsSpecialOfferOpen(true);
    }
  };

  const onSpecialOfferContinue = () => {
    setIsSpecialOfferOpen(false);
    openPayment('special-offer');
  };

  const openPayment = (source: string) => {
    Analytics.trackEvent(`step${currentIndex + 1}`, 'complete', { page: 'hairstyle-women-paywall', source });
    if (isPaymentReady && ppRef.current) {
      ppRef.current.showPaymentPopup();
    } else {
      setPendingOpen(true);
    }
  };

  useEffect(() => {
    if (pendingOpen && isPaymentReady && ppRef.current) {
      setPendingOpen(false);
      ppRef.current.showPaymentPopup();
    }
  }, [pendingOpen, isPaymentReady]);

  return (
    <div className={styles.page}>
      <h1 className={styles.reportTitle}>{t('hairstyleWomenPaywall.reportTitle')}</h1>
      <p className={styles.reportSub}>{t('hairstyleWomenPaywall.reportSub')}</p>

      {/* Before / After — your selfie vs the locked "after" */}
      {front && (
        <div className={styles.beforeAfter}>
          <div className={styles.baCol}>
            <div className={styles.baPhoto}><img src={front} alt="" /></div>
            <span className={styles.baLabel}>{t('hairstyleWomenPaywall.before')}</span>
          </div>
          <div className={styles.baCol}>
            <div className={styles.baPhoto}>
              <img src={front} alt="" className={styles.baBlur} />
              <span className={styles.baLock}>🔒</span>
            </div>
            <span className={styles.baLabel}>{t('hairstyleWomenPaywall.after')}</span>
          </div>
        </div>
      )}

      {/* Report card: before/after sliders (Attractiveness, Confidence) + locked tease */}
      <section className={styles.card}>
        <div className={styles.sliders}>
          {[
            { key: 'attractiveness', pink: false },
            { key: 'confidence', pink: true },
          ].map((m) => (
            <div key={m.key} className={styles.sliderRow}>
              {(['low', 'high'] as const).map((side) => (
                <div key={side} className={`${styles.sliderCell} ${side === 'high' ? styles.cellHigh : ''}`}>
                  <span className={styles.sliderName}>{t(`hairstyleWomenPaywall.${m.key}`)}</span>
                  <div className={`${styles.slider} ${m.pink ? styles.sliderPink : ''} ${side === 'high' ? styles.sliderHigh : ''}`}>
                    <i className={styles.sliderKnob} />
                  </div>
                  <span className={styles.sliderEnd}>{t(`hairstyleWomenPaywall.${side}`)}</span>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className={styles.lockRow}>
          <span className={styles.lockPill}>🔒 {t('hairstyleWomenPaywall.locked')}</span>
        </div>

        <div className="sub-static-btn">
          <PrimaryButton sticky={false} loading={pendingOpen} onClick={() => openPayment('inline')}>{t('hairstyleWomenPaywall.inlineCta')}</PrimaryButton>
        </div>
        {!!priceTerms && <p className={styles.reportBilling}>{priceTerms}</p>}
      </section>

      {/* What you'll unlock */}
      <section className={styles.block}>
        <h2 className={styles.blockTitle}>{t('hairstyleWomenPaywall.unlockTitle')}</h2>
        <ul className={styles.value}>
          {VALUE_KEYS.map((k) => (
            <li key={k}><span className={styles.check}>✓</span>{t(`hairstyleWomenPaywall.value.${k}`)}</li>
          ))}
        </ul>
      </section>

      {/* Features */}
      <section className={styles.block}>
        <h2 className={styles.blockTitle}>{t('hairstyleWomenPaywall.featuresTitle')}</h2>
        <ul className={styles.features}>
          {FEATURES.map((f) => (
            <li key={f.key} className={styles.feature}>
              <span className={styles.featureIcon}>{f.emoji}</span>
              <div>
                <p className={styles.featureTitle}>{t(`hairstyleWomenPaywall.features.${f.key}.title`)}</p>
                <p className={styles.featureDesc}>{t(`hairstyleWomenPaywall.features.${f.key}.desc`)}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* Reviews */}
      <section className={styles.block}>
        <h2 className={styles.blockTitle}>{t('hairstyleWomenPaywall.reviewsTitle')}</h2>
        {REVIEWS.map((r) => (
          <div key={r.nameKey} className={styles.review}>
            <div className={styles.reviewTop}>
              <img src={reviewPhoto(r.n, g)} alt="" className={styles.reviewPhoto} />
              <div className={styles.reviewMeta}>
                <span className={styles.reviewName}>{t(`hairstyleWomenPaywall.reviews.${r.nameKey}${g === 'woman' ? 'W' : ''}`)}</span>
                <span className={styles.reviewStars}>★★★★★</span>
              </div>
            </div>
            <p className={styles.reviewText}>{t(`hairstyleWomenPaywall.reviews.${r.textKey}${g === 'woman' ? 'W' : ''}`)}</p>
          </div>
        ))}
      </section>

      {/* Safe checkout */}
      <section className={styles.safe}>
        <p className={styles.terms}>
          {t('onboarding.personalPlan.plan.accept')}{' '}
          <a onClick={() => setPage('privacy-policy')}>{t('onboarding.personalPlan.plan.privacy')}</a>
          , <a onClick={() => setPage('terms-of-use')}>{t('onboarding.personalPlan.plan.terms')}</a>
        </p>
        {!!priceTerms && <p className={styles.billingDesc}>{priceTerms}</p>}
        <p className={styles.safeText}>🔒 {t('hairstyleWomenPaywall.safeText')}</p>
        <div className={styles.cardIcons}>
          <VisaIcon /><MastercardIcon /><AmexIcon /><DiscoverIcon /><JcbIcon />
        </div>
      </section>

      <footer className={styles.footer}>
        <a className={styles.footerContact} href={`mailto:${SUPPORT_EMAIL}`}>
          {t('hairstylePaywall.contact')}
        </a>
        <p className={styles.footerAddress}>
          {COMPANY}
          <br />
          {COMPANY_ADDRESS}
        </p>
        <div className={styles.footerLinks}>
          <a onClick={() => setPage('privacy-policy')}>
            {t('onboarding.personalPlan.plan.privacy')}
          </a>
          <a onClick={() => setPage('terms-of-use')}>
            {t('onboarding.personalPlan.plan.terms')}
          </a>
        </div>
      </footer>

      <PrimaryButton hidden={!showSticky} loading={pendingOpen} onClick={() => openPayment('sticky')}>{t('hairstyleWomenPaywall.cta')}</PrimaryButton>

      {showEmailPopup && (
        <EmailPopup isOpen={isEmailPopupOpen} onClose={handleEmailPopupClose} />
      )}

      <SpecialOfferPopup
        isOpen={isSpecialOfferOpen}
        config={specialOffer}
        product={activeProduct}
        onContinue={onSpecialOfferContinue}
        onClose={() => setIsSpecialOfferOpen(false)}
      />

      {activeProduct && (
        <PaymentProcessor
          ref={ppRef}
          activeProduct={activeProduct}
          orderDetails={orderDetails}
          onSuccess={onPaymentSuccess}
          onError={onPaymentError}
          onClose={onPaymentClose}
        />
      )}
    </div>
  );
}

export default Page;
